import { Component, Input, Output, EventEmitter, forwardRef, HostListener, ElementRef, OnChanges, SimpleChanges, Injector, OnInit, inject } from '@angular/core'

import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
  Validators,
  NgControl,
} from '@angular/forms'
import { TypographyComponent } from '../typography'
import { IconComponent } from '../icon'
import { DropdownMenuComponent } from '../dropdown-menu'
import { DropdownMenuItemComponent } from '../dropdown-menu-item'
import { DropdownMenuSectionHeaderComponent } from '../dropdown-menu-section-header'
import { TagComponent } from '../tag'
import { SelectOption, SelectState } from '../select'

@Component({
  selector: 'hyperkit-multiselect',
  standalone: true,
  imports: [
    FormsModule,
    TypographyComponent,
    TagComponent,
    IconComponent,
    DropdownMenuComponent,
    DropdownMenuSectionHeaderComponent,
    DropdownMenuItemComponent
],
  templateUrl: './multiselect.component.html',
  styleUrls: ['./multiselect.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectComponent),
      multi: true,
    },
  ],
})
export class MultiSelectComponent
  implements ControlValueAccessor, OnChanges, OnInit
{
  private injector = inject(Injector);
  private elementRef = inject(ElementRef);

  @Input() label = ''
  @Input() showLabel = true
  @Input() placeholder = 'Select...'
  @Input() state: SelectState = 'active'
  @Input() disabled = false
  @Input() sectionHeader?: string
  @Input() maxDisplayChips = 3 // Maximum number of chips to display before showing overflow

  // Consolidated single options input
  @Input() options: any[] = []

  // Configuration for object handling
  @Input() displayProperty = 'name' // Property to use for display text
  @Input() valueProperty = 'id' // Property to use for value

  @Output() selectionChange = new EventEmitter<SelectOption[]>()

  // Internal options that will be used in the template
  internalOptions: SelectOption[] = []

  selectedOptions: SelectOption[] = []
  visibleChips: SelectOption[] = []
  overflowCount = 0

  isOpen = false
  private isHovering = false
  private originalState: SelectState = 'active'

  // Store the pending value until options are available
  private pendingValue: any = null
  public ngControl: NgControl | null = null

  // Control Value Accessor
  private onChange: (value: any) => void = () => {}
  private onTouched: () => void = () => {}

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null)
    if (this.ngControl) {
      this.ngControl.valueAccessor = this
    }
  }

  get isRequired(): boolean {
    return !!this.ngControl?.control?.hasValidator?.(Validators.required)
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Process options when inputs change
    if (
      changes['options'] ||
      changes['displayProperty'] ||
      changes['valueProperty']
    ) {
      this.processOptions()

      // If we have a pending value and options are now available, process it
      if (this.pendingValue !== null && this.internalOptions.length > 0) {
        this.processValue(this.pendingValue)
        this.pendingValue = null
      }
    }
  }

  private processOptions(): void {
    if (!this.options || !Array.isArray(this.options)) {
      this.internalOptions = []
      return
    }

    this.internalOptions = this.options.map((item) => {
      // Handle if item is already in SelectOption format
      if (item && typeof item === 'object' && 'id' in item && 'text' in item) {
        return item as SelectOption
      }

      // Handle if item is a string
      if (typeof item === 'string') {
        return {
          id: item,
          text: item,
        }
      }

      // Handle if item is an object
      if (item && typeof item === 'object') {
        return {
          id: item[this.valueProperty],
          text: item[this.displayProperty]?.toString() || 'Unknown',
          originalObject: item, // Store the original object for reference if needed
        }
      }

      // Fallthrough case
      return {
        id: String(item),
        text: String(item),
      }
    })

    // Update selected options based on the new internal options
    this.updateSelectedOptionsFromInternalOptions()
    this.calculateVisibleChips()
  }

  private updateSelectedOptionsFromInternalOptions(): void {
    // Keep only selected options that still exist in internalOptions
    this.selectedOptions = this.selectedOptions.filter((selected) =>
      this.internalOptions.some((option) => option.id === selected.id),
    )
  }

  writeValue(value: any): void {
    // If options aren't available yet, store the value for later processing
    if (
      this.internalOptions.length === 0 &&
      value !== null &&
      value !== undefined
    ) {
      this.pendingValue = value
      return
    }

    this.processValue(value)
  }

  private processValue(value: any): void {
    if (value !== null && value !== undefined) {
      if (Array.isArray(value)) {
        // Handle array of IDs
        this.selectedOptions = this.internalOptions.filter((option) =>
          value.includes(option.id),
        )
      } else {
        // Handle single value
        const option = this.internalOptions.find((opt) => opt.id === value)
        if (option) {
          this.selectedOptions = [option]
        } else {
          this.selectedOptions = []
        }
      }

      if (
        this.selectedOptions.length > 0 &&
        this.state !== 'error' &&
        this.state !== 'inactive'
      ) {
        this.state = 'filled'
      }

      this.calculateVisibleChips()
    } else {
      this.selectedOptions = []
      this.calculateVisibleChips()
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
  }

  toggleDropdown(): void {
    if (this.disabled) return

    this.isOpen = !this.isOpen
    if (this.isOpen) {
      this.state = 'focused'
      this.onTouched()
    } else {
      this.state = this.originalState
    }
  }

  toggleOption(option: SelectOption, event?: Event): void {
    if (event) {
      event.stopPropagation()
    }

    const index = this.selectedOptions.findIndex(
      (selected) => selected.id === option.id,
    )

    if (index === -1) {
      // Add option to selection
      this.selectedOptions = [...this.selectedOptions, option]
    } else {
      // Remove option from selection
      this.selectedOptions = this.selectedOptions.filter(
        (selected) => selected.id !== option.id,
      )
    }

    // Update form control with array of IDs
    const selectedIds = this.selectedOptions.map((opt) => opt.id)
    this.onChange(selectedIds)
    this.onTouched()

    // Emit selection event with array of selected options
    this.selectionChange.emit(this.selectedOptions)

    if (
      this.selectedOptions.length > 0 &&
      this.state !== 'error' &&
      this.state !== 'inactive'
    ) {
      this.state = 'filled'
    } else if (
      this.selectedOptions.length === 0 &&
      this.state !== 'error' &&
      this.state !== 'inactive'
    ) {
      this.state = 'active'
    }

    this.calculateVisibleChips()
  }

  isOptionSelected(option: SelectOption): boolean {
    return this.selectedOptions.some((selected) => selected.id === option.id)
  }

  removeChip(option: SelectOption, event: Event): void {
    event.stopPropagation()
    this.toggleOption(option)
  }

  calculateVisibleChips(): void {
    if (this.selectedOptions.length <= this.maxDisplayChips) {
      this.visibleChips = [...this.selectedOptions]
      this.overflowCount = 0
    } else {
      this.visibleChips = this.selectedOptions.slice(0, this.maxDisplayChips)
      this.overflowCount = this.selectedOptions.length - this.maxDisplayChips
    }
  }

  onMouseEnter(): void {
    if (this.disabled) return

    this.isHovering = true
    if (
      this.state !== 'focused' &&
      this.state !== 'inactive' &&
      this.state !== 'error'
    ) {
      this.state = 'hover'
    }
  }

  onMouseLeave(): void {
    if (this.disabled) return

    this.isHovering = false
    if (this.state === 'hover') {
      this.state = this.originalState
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target) && this.isOpen) {
      this.isOpen = false
      this.state = this.originalState
      this.onTouched()
    }
  }
}
